import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { Button } from '@clientbase/clientbase-library'
import usePushSubscription from 'hooks/usePushSubscription'

import * as S from './PushOptInCard.styles'

const DISMISS_KEY = 'push-opt-in-dismissed'
const DISMISS_TTL_MS = 14 * 24 * 60 * 60 * 1000

const isStandaloneMode = (): boolean => {
  if (typeof window === 'undefined') return false
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true
  const nav = window.navigator as Navigator & { standalone?: boolean }
  return nav.standalone === true
}

const isDismissed = (): boolean => {
  if (typeof window === 'undefined') return true
  const ts = localStorage.getItem(DISMISS_KEY)
  if (!ts) return false
  const dismissedAt = parseInt(ts, 10)
  if (Number.isNaN(dismissedAt)) return false
  return Date.now() - dismissedAt < DISMISS_TTL_MS
}

const BellIcon = ({ size = 22 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
  </svg>
)

const PushOptInCard = () => {
  const { status, isSubscribed, subscribe } = usePushSubscription()
  const [dismissed, setDismissed] = useState(true)
  const [notificationApiAvailable, setNotificationApiAvailable] =
    useState(false)
  const [isStandalone, setIsStandalone] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return
    setNotificationApiAvailable('Notification' in window)
    setDismissed(isDismissed())
    setIsStandalone(isStandaloneMode())
  }, [])

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, Date.now().toString())
    setDismissed(true)
  }

  const handleEnable = async () => {
    const ok = await subscribe()
    if (ok) {
      toast.success('Notificações ativadas neste dispositivo.')
    } else if (status === 'denied') {
      toast.error(
        'Permissão de notificações negada. Ative nas configurações do navegador.'
      )
    } else {
      toast.error('Não foi possível ativar as notificações.')
    }
  }

  if (!isStandalone) return null
  if (!notificationApiAvailable) return null
  if (status === 'unsupported') return null
  if (isSubscribed) return null
  if (typeof window !== 'undefined' && Notification.permission === 'denied') {
    return null
  }
  if (dismissed) return null

  return (
    <S.Wrapper>
      <S.Header>
        <S.IconBox>
          <span style={{ color: '#ffffff', display: 'flex' }}>
            <BellIcon size={22} />
          </span>
        </S.IconBox>
        <S.Texts>
          <S.Title>Ative as notificações</S.Title>
          <S.Body>
            Receba avisos sobre cobranças, contratos e eventos importantes mesmo
            com o sistema fechado.
          </S.Body>
        </S.Texts>
      </S.Header>
      <S.Actions>
        <S.DismissButton onClick={handleDismiss} type="button">
          Agora não
        </S.DismissButton>
        <Button
          variantButton="primary"
          onClick={handleEnable}
          loading={status === 'loading'}
          disabled={status === 'loading'}
        >
          Ativar
        </Button>
      </S.Actions>
    </S.Wrapper>
  )
}

export default PushOptInCard
